/**
 * Styles for the Ability Test Rig diagnostic panel family.
 */
import React from 'react';
import {
  COLORS,
  FONTS,
  FONT_SIZE,
  FONT_WEIGHT,
  PLAYER_UI,
  RADII,
  SPACE,
} from '../../theme';

export const rigStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.sm,
    height: '100%',
    overflowY: 'auto',
    padding: SPACE.xs,
    boxSizing: 'border-box',
  },
  sectionCard: {
    background: 'rgba(15, 20, 28, 0.75)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.md,
    padding: SPACE.sm,
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.sm,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: PLAYER_UI.brassBright,
    fontSize: FONT_SIZE.xs,
    fontWeight: FONT_WEIGHT.bold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.xs,
    background: 'rgba(0, 0, 0, 0.45)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    padding: `4px ${SPACE.sm}px`,
  },
  searchIcon: {
    fontSize: FONT_SIZE.sm,
    opacity: 0.6,
  },
  searchInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: COLORS.textPrimary,
    fontFamily: FONTS.mono,
    fontSize: FONT_SIZE.sm,
  },
  clearSearchBtn: {
    background: 'transparent',
    border: 'none',
    color: COLORS.textMuted,
    cursor: 'pointer',
    fontSize: FONT_SIZE.xs,
    padding: '2px 4px',
  },
  lastCastBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 10px',
    background: 'rgba(230, 160, 40, 0.08)',
    border: `1px solid ${PLAYER_UI.border}`,
    borderRadius: RADII.sm,
    fontSize: FONT_SIZE.xs,
    fontFamily: FONTS.mono,
  },
  abilityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    maxHeight: 220,
    overflowY: 'auto',
    background: 'rgba(10, 14, 20, 0.6)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    padding: 4,
  },
  abilityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.sm,
    padding: '5px 8px',
    background: 'rgba(255, 255, 255, 0.03)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'left',
    width: '100%',
    boxSizing: 'border-box',
  },
  abilityRowActive: {
    background: 'rgba(230, 160, 40, 0.16)',
    borderColor: PLAYER_UI.brassBright,
    boxShadow: '0 0 10px rgba(196, 164, 105, 0.3)',
  },
  abilityInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  abilityName: {
    color: COLORS.textPrimary,
    fontSize: FONT_SIZE.sm,
    fontWeight: FONT_WEIGHT.medium,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  abilityId: {
    color: COLORS.textMuted,
    fontFamily: FONTS.mono,
    fontSize: 9,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  abilityMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  costChip: {
    fontSize: 10,
    color: COLORS.info,
    background: 'rgba(74, 144, 226, 0.12)',
    padding: '1px 5px',
    borderRadius: RADII.sm,
    fontFamily: FONTS.mono,
    fontWeight: FONT_WEIGHT.bold,
  },
  cdChip: {
    fontSize: 10,
    color: COLORS.warning,
    background: 'rgba(230, 160, 40, 0.12)',
    padding: '1px 5px',
    borderRadius: RADII.sm,
    fontFamily: FONTS.mono,
  },
  inspectorCard: {
    background: 'rgba(12, 16, 24, 0.85)',
    border: `1px solid ${PLAYER_UI.border}`,
    borderRadius: RADII.md,
    padding: SPACE.sm,
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
  },
  factsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: SPACE.xs,
  },
  factTile: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
    padding: '3px 4px',
    textAlign: 'center',
  },
  factLabel: {
    fontSize: 8,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
  },
  factVal: {
    fontSize: 10,
    fontWeight: FONT_WEIGHT.bold,
    color: COLORS.textPrimary,
    fontFamily: FONTS.mono,
  },
  overrideGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: SPACE.sm,
  },
  overrideCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: SPACE.xs,
    padding: '10px 12px',
    background: 'rgba(20, 24, 32, 0.8)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.md,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'left',
  },
  overrideCardActive: {
    background: 'rgba(81, 207, 102, 0.12)',
    borderColor: COLORS.success,
    boxShadow: '0 0 12px rgba(81, 207, 102, 0.25)',
  },
  overrideTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  overrideTitle: {
    fontSize: FONT_SIZE.sm,
    fontWeight: FONT_WEIGHT.bold,
    color: COLORS.textPrimary,
  },
  overrideBottomRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  commandChip: {
    fontSize: 9,
    fontFamily: FONTS.mono,
    color: COLORS.textMuted,
    background: 'rgba(255, 255, 255, 0.05)',
    padding: '2px 5px',
    borderRadius: RADII.sm,
    border: `1px solid ${COLORS.borderSubtle}`,
  },
  hotkeyCard: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.sm,
    padding: '6px 8px',
    background: 'rgba(255, 255, 255, 0.03)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.sm,
  },
  keyBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 26,
    height: 24,
    padding: '0 6px',
    background: 'rgba(116, 192, 252, 0.12)',
    border: '1px solid rgba(116, 192, 252, 0.35)',
    borderRadius: RADII.sm,
    color: COLORS.info,
    fontFamily: FONTS.mono,
    fontSize: FONT_SIZE.xs,
    fontWeight: FONT_WEIGHT.bold,
  },
  hotkeySelect: {
    flex: 1,
    background: 'rgba(0, 0, 0, 0.4)',
    border: `1px solid ${COLORS.borderSubtle}`,
    color: COLORS.textPrimary,
    padding: '4px 8px',
    fontFamily: FONTS.mono,
    fontSize: FONT_SIZE.xs,
    borderRadius: RADII.sm,
    outline: 'none',
  },
  rotationCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
    padding: SPACE.sm,
    background: 'rgba(20, 24, 32, 0.75)',
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.md,
  },
  rotationCardActive: {
    borderColor: PLAYER_UI.brassBright,
    boxShadow: '0 0 10px rgba(196, 164, 105, 0.25)',
  },
  rotationHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  stepStrip: {
    display: 'flex',
    gap: 4,
    overflowX: 'auto',
    padding: '4px 0',
  },
  stepPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 9,
    fontFamily: FONTS.mono,
    padding: '2px 5px',
    borderRadius: RADII.sm,
    background: 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${COLORS.borderSubtle}`,
    color: COLORS.textSecondary,
    whiteSpace: 'nowrap',
  },
  stepPillActive: {
    background: 'rgba(230, 160, 40, 0.2)',
    borderColor: PLAYER_UI.brassBright,
    color: COLORS.textPrimary,
    fontWeight: FONT_WEIGHT.bold,
  },
};
